import CollapsibleSidebarLayout from "components/collapsible-sidebar-layout/collapsible-sidebar-layout";
import { useCallback, useContext, useEffect, useMemo } from "react";
import FeatureCommentsTabComponent from "components/feature-activity-card/feature-comments-tab";
import { useCommentsCountLabel } from "components/feature-activity-card/use-comments-count-label";
import { CloseOutlined } from "@ant-design/icons";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import ErrorBoundary from "components/error-boundary";
import { ProjectPermissionEnum } from "../../constants";

import {
  MaterialLogProvider,
  useMaterialLogContext
} from "./material-log-context";
import MaterialLog from "./material-log";

// Re-export for convenience
export { useMaterialLogContext } from "./material-log-context";

function MaterialLogSidebarHeader({
  title,
  onClose,
  caption
}: {
  title: string;
  onClose: () => void;
  caption: string;
}) {
  return (
    <div
      className="flex items-center justify-between"
      style={{ borderBottom: "1px solid var(--ciq-gray-950-a30)" }}
    >
      <div
        className="p-4 flex items-center overflow-hidden"
        data-testid="material-log-sidebar-header"
      >
        <span className="shrink-0 sidepanel-header-title">{title}</span>
        <span className="shrink-0 text-xs font-normal ml-2 mr-2">|</span>
        <span
          className="text-xs font-normal truncate min-w-0"
          title={caption || ""}
        >
          {caption || ""}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="border-none bg-transparent cursor-pointer p-2"
      >
        <CloseOutlined />
      </button>
    </div>
  );
}

// Inner component that uses the context
function MaterialLogPageContent() {
  const {
    isSidebarOpen,
    setIsSidebarOpen,
    sidebarMetadata,
    setSidebarMetadata
  } = useMaterialLogContext();

  const { tokenContents, isFeatureFlagEnabled, isProjectArchived } =
    useContext(ProjectContext);

  const isAttachementFeatureEnabled =
    !isFeatureFlagEnabled("DISABLE_ATTACHMENT");

  const { label: sidebarTitle, onCommentsCountChange } = useCommentsCountLabel(
    sidebarMetadata?.itemDetails?.id
  );

  const sidebarAction = useCallback(
    (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => {
      if (params.action === "open") {
        setIsSidebarOpen(true);
        setSidebarMetadata({
          feature: params.feature,
          itemDetails: params.itemDetailsToSet
        });
      } else {
        setIsSidebarOpen(false);
        setSidebarMetadata(null);
      }
    },
    [setIsSidebarOpen, setSidebarMetadata]
  );

  // hook to close the sidebar when esc key is pressed
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isSidebarOpen) {
        sidebarAction({ feature: "", action: "close", itemDetailsToSet: null });
      }
    };
    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isSidebarOpen, setIsSidebarOpen, sidebarAction]);

  const permissionsForComments = useMemo(() => {
    const permissions = [];

    const canCreateComment = !isPermissionNotGrantted(
      ProjectPermissionEnum.CreateMaterialComment,
      tokenContents?.role ?? ""
    );

    const canCreateMaterialCommentIfRC =
      sidebarMetadata?.itemDetails?.responsible_contractor?.id ===
      tokenContents?.claims["x-hasura-vendor-id"];

    if (
      !isProjectArchived &&
      (canCreateComment || canCreateMaterialCommentIfRC)
    ) {
      permissions.push("create_comment");
    }

    return permissions;
  }, [
    tokenContents?.claims,
    tokenContents?.role,
    sidebarMetadata?.itemDetails,
    isProjectArchived
  ]);

  return (
    <CollapsibleSidebarLayout
      sidebarState={[isSidebarOpen, () => {}]}
      sidebarWidth="420px"
      sidebarHeader={
        <ErrorBoundary>
          <MaterialLogSidebarHeader
            title={sidebarTitle}
            caption={sidebarMetadata?.itemDetails?.title || ""}
            onClose={() => {
              setIsSidebarOpen(false);
              sidebarAction({
                feature: "",
                action: "close",
                itemDetailsToSet: null
              });
            }}
          />
        </ErrorBoundary>
      }
      sidebarContent={
        sidebarMetadata?.itemDetails ? (
          <ErrorBoundary>
            <FeatureCommentsTabComponent
              key={sidebarMetadata?.itemDetails!.id}
              permissions={permissionsForComments}
              featureKey="feature_instance_id"
              featureId={sidebarMetadata?.itemDetails!.id}
              isAttachementFeatureEnabled={isAttachementFeatureEnabled}
              auditFeatureType="MATERIALS"
              data-testid="fmch-comments-tab"
              paddingX="px-6"
              onCommentsCountChange={onCommentsCountChange}
            />
          </ErrorBoundary>
        ) : null
      }
      mainContent={
        <ErrorBoundary>
          <MaterialLog
            pageSidebar={{
              sidebarAction,
              sidebarMetadata,
              isSidebarOpen
            }}
          />
        </ErrorBoundary>
      }
      placement="right"
    />
  );
}

// Main page component
function MaterialLogPage() {
  return (
    <MaterialLogProvider>
      <MaterialLogPageContent />
    </MaterialLogProvider>
  );
}

export default MaterialLogPage;
